// Client-side photo → B&W stamp filters. All output is pure black or white for print.
// filter: "no_effect"      → original photo, unmodified, full color
//         "sobel"          → edge detection outline (default)
//         "threshold"      → high-contrast B&W silhouette
//         "pencil"         → dodge-blend pencil sketch (grayscale, thresholded to B&W for print)
//         "halftone"       → dotted halftone from luminance
//         "charcoal_sketch"→ dodge-blend charcoal sketch, smooth continuous grayscale (not thresholded)
//         "canvas_print"   → full-color canvas-weave texture + light contrast/vignette
//
// Input: File / Blob. Output: PNG Blob.

// Single source of truth for the customer-facing filter list — shared by the seller's
// "Photo filter" checkboxes (TemplateEditor) and the buyer's filter dropdown (Customizer).
export const FACE_FILTERS = [
  { key: "no_effect", label: "No effect" },
  { key: "sobel", label: "Outline (Sobel edges)" },
  { key: "threshold", label: "High-contrast B&W silhouette" },
  { key: "pencil", label: "Pencil sketch" },
  { key: "halftone", label: "Halftone dots" },
  { key: "charcoal_sketch", label: "Charcoal Sketch" },
  { key: "canvas_print", label: "Canvas Print" },
];

const MAX_SIDE = 1200;

function loadImageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function makeCanvas(w, h) {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return c;
}

function toGrayscale(imgData) {
  const d = imgData.data;
  const g = new Uint8ClampedArray(imgData.width * imgData.height);
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    g[j] = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) | 0;
  }
  return g;
}

function whiteImage(w, h) {
  const out = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < out.length; i += 4) { out[i] = out[i + 1] = out[i + 2] = 255; out[i + 3] = 255; }
  return out;
}

function paintPixel(out, i, val) {
  const oi = i * 4;
  out[oi] = out[oi + 1] = out[oi + 2] = val;
  out[oi + 3] = 255;
}

// Simple separable Gaussian blur on a grayscale plane (3-pixel radius, one pass).
function boxBlur(gray, w, h, radius = 3) {
  const dst = new Uint8ClampedArray(gray.length);
  // horizontal
  const tmp = new Uint8ClampedArray(gray.length);
  const win = radius * 2 + 1;
  for (let y = 0; y < h; y++) {
    let sum = 0;
    for (let k = -radius; k <= radius; k++) sum += gray[y * w + Math.min(w - 1, Math.max(0, k))];
    for (let x = 0; x < w; x++) {
      tmp[y * w + x] = (sum / win) | 0;
      const rem = gray[y * w + Math.max(0, x - radius)];
      const add = gray[y * w + Math.min(w - 1, x + radius + 1)];
      sum += add - rem;
    }
  }
  // vertical
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let k = -radius; k <= radius; k++) sum += tmp[Math.min(h - 1, Math.max(0, k)) * w + x];
    for (let y = 0; y < h; y++) {
      dst[y * w + x] = (sum / win) | 0;
      const rem = tmp[Math.max(0, y - radius) * w + x];
      const add = tmp[Math.min(h - 1, y + radius + 1) * w + x];
      sum += add - rem;
    }
  }
  return dst;
}

function applySobel(gray, w, h, out, threshold = 42) {
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const tl = gray[(y - 1) * w + (x - 1)], t = gray[(y - 1) * w + x], tr = gray[(y - 1) * w + (x + 1)];
      const l  = gray[y * w + (x - 1)],       r = gray[y * w + (x + 1)];
      const bl = gray[(y + 1) * w + (x - 1)], b = gray[(y + 1) * w + x], br = gray[(y + 1) * w + (x + 1)];
      const gx = (-tl - 2 * l - bl + tr + 2 * r + br);
      const gy = (-tl - 2 * t - tr + bl + 2 * b + br);
      const mag = Math.sqrt(gx * gx + gy * gy);
      if (mag > threshold) paintPixel(out, y * w + x, 0);
    }
  }
}

function applyThreshold(gray, w, h, out, cutoff = 128) {
  for (let i = 0; i < gray.length; i++) {
    paintPixel(out, i, gray[i] < cutoff ? 0 : 255);
  }
}

function applyPencil(gray, w, h, out, cutoff = 160) {
  // Dodge blend: result = min(255, gray * 255 / (255 - blurredInvert))
  const inv = new Uint8ClampedArray(gray.length);
  for (let i = 0; i < gray.length; i++) inv[i] = 255 - gray[i];
  const blurred = boxBlur(inv, w, h, 6);
  for (let i = 0; i < gray.length; i++) {
    const denom = 255 - blurred[i];
    const dodged = denom <= 0 ? 255 : Math.min(255, (gray[i] * 255) / denom);
    // Threshold pencil to B&W for print-safe output
    paintPixel(out, i, dodged < cutoff ? 0 : 255);
  }
}

// Same dodge-blend base as `applyPencil` but kept as smooth continuous-tone grayscale (no
// black/white threshold) plus a mild contrast stretch and a touch of grain, for a softer
// hand-drawn charcoal look rather than a print-safe silhouette.
function applyCharcoalSketch(gray, w, h, out) {
  const inv = new Uint8ClampedArray(gray.length);
  for (let i = 0; i < gray.length; i++) inv[i] = 255 - gray[i];
  const blurred = boxBlur(inv, w, h, 6);
  for (let i = 0; i < gray.length; i++) {
    const denom = 255 - blurred[i];
    const dodged = denom <= 0 ? 255 : Math.min(255, (gray[i] * 255) / denom);
    const contrasted = (dodged - 128) * 1.15 + 128;
    const grain = (Math.random() - 0.5) * 10;
    paintPixel(out, i, contrasted + grain);
  }
}

// Full-color canvas-weave texture: mild contrast boost + a procedural woven-fabric ripple
// (two orthogonal sine waves) + a light corner vignette, mimicking a gallery canvas print.
// Operates directly on the source color ImageData (mutated in place), unlike the grayscale
// filters above.
function applyCanvasPrint(imgData, w, h) {
  const d = imgData.data;
  const cx = w / 2, cy = h / 2;
  const maxDist = Math.hypot(cx, cy) || 1;
  const weavePeriod = 4, weaveAmp = 9;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const wave = Math.sin((x / weavePeriod) * Math.PI) * Math.sin((y / weavePeriod) * Math.PI);
      const tex = wave * weaveAmp;
      const dist = Math.hypot(x - cx, y - cy) / maxDist;
      const vig = 1 - Math.min(0.35, dist * dist * 0.45);
      d[i] = ((d[i] - 128) * 1.08 + 128 + tex) * vig;
      d[i + 1] = ((d[i + 1] - 128) * 1.08 + 128 + tex) * vig;
      d[i + 2] = ((d[i + 2] - 128) * 1.08 + 128 + tex) * vig;
    }
  }
}

function applyHalftone(gray, w, h, out, cell = 6) {
  // Divide image into cells; each cell becomes a black filled circle whose radius scales with darkness.
  for (let cy = 0; cy < h; cy += cell) {
    for (let cx = 0; cx < w; cx += cell) {
      // Average luminance of the cell
      let sum = 0, count = 0;
      for (let y = cy; y < Math.min(h, cy + cell); y++) {
        for (let x = cx; x < Math.min(w, cx + cell); x++) {
          sum += gray[y * w + x]; count++;
        }
      }
      const avg = count ? sum / count : 255;
      const darkness = 1 - avg / 255;              // 0 (white) → 1 (black)
      const maxR = cell / 2;
      const rad = darkness * maxR;
      if (rad < 0.4) continue;
      const centerX = cx + cell / 2;
      const centerY = cy + cell / 2;
      const r2 = rad * rad;
      const yStart = Math.max(0, Math.floor(centerY - rad));
      const yEnd   = Math.min(h - 1, Math.ceil(centerY + rad));
      const xStart = Math.max(0, Math.floor(centerX - rad));
      const xEnd   = Math.min(w - 1, Math.ceil(centerX + rad));
      for (let y = yStart; y <= yEnd; y++) {
        for (let x = xStart; x <= xEnd; x++) {
          const dx = x - centerX, dy = y - centerY;
          if (dx * dx + dy * dy <= r2) paintPixel(out, y * w + x, 0);
        }
      }
    }
  }
}

export async function sketchify(fileOrBlob, { filter = "sobel" } = {}) {
  const img = await loadImageFromBlob(fileOrBlob);
  const iw = img.naturalWidth, ih = img.naturalHeight;
  const scale = Math.min(1, MAX_SIDE / Math.max(iw, ih));
  const w = Math.max(2, Math.round(iw * scale));
  const h = Math.max(2, Math.round(ih * scale));

  const cv = makeCanvas(w, h);
  const ctx = cv.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const src = ctx.getImageData(0, 0, w, h);

  const key = (filter || "sobel").toLowerCase();

  // Full-color paths — operate on the source ImageData directly, no grayscale conversion.
  if (key === "no_effect" || key === "none") {
    return await new Promise((resolve) => cv.toBlob(resolve, "image/png"));
  }
  if (key === "canvas_print") {
    applyCanvasPrint(src, w, h);
    ctx.putImageData(src, 0, 0);
    return await new Promise((resolve) => cv.toBlob(resolve, "image/png"));
  }

  // Grayscale/B&W paths
  const gray = toGrayscale(src);
  const out = whiteImage(w, h);
  switch (key) {
    case "threshold":       applyThreshold(gray, w, h, out); break;
    case "pencil":          applyPencil(gray, w, h, out); break;
    case "halftone":        applyHalftone(gray, w, h, out); break;
    case "charcoal_sketch": applyCharcoalSketch(gray, w, h, out); break;
    case "sobel":
    case "outline":
    default:                applySobel(gray, w, h, out); break;
  }

  ctx.putImageData(new ImageData(out, w, h), 0, 0);
  return await new Promise((resolve) => cv.toBlob(resolve, "image/png"));
}
